<template>
  <header class="webinar__header">
    <div class="screen-wrapper">
      <a :href="mainPage" class="webinar__header__login">
        <img v-if="logo" :src="`${baseURI}uploads/160-40/${logo}`"
             :srcset="`${baseURI}uploads/320-80/${logo} 2x`" alt=""/>
      </a>
      <div class="webinar__header__links">
        <a v-for="(link, index) in webinar.links" :key="index" target="_blank"
           class="webinar__header__link"
           :href="addUtm(link.href)">
          {{ link.label }}
          <icon-arrow-alt/>
        </a>
      </div>
    </div>
  </header>
  <div
    :class="['webinar-wrapper', 'webinar__page', { 'webinar__page--fullscreen': fullscreen }]">
    <div class="webinar__title">
      <div class="webinar__title__text">{{ webinar.title }}</div>
      <div class="webinar__reload" @click="reload"
           :title="t('public.reload')">
        <icon-reload/>
      </div>
    </div>
    <div class="webinar__body">
      <you-tube-video :webinar="webinar" :schedule="schedule"
                      :archived="archived"
                      @change-fullscreen="changeFullscreen"/>
      <chat :schedule-id="schedule.id" :started="schedule.left >= 0"
            :archived="archived"
            :disabled="archived || schedule.finished || schedule.left < 0"/>
    </div>
    <div class="webinar__description" v-html="webinar.description"
         v-if="schedule.left < 0"></div>
    <template v-else>
      <admin-buttons v-if="administration" :schedule-id="schedule.id"/>
      <buttons v-else :schedule-id="schedule.id"/>
      <div class="webinar__finish-button" @click="finishTranslation"
           v-if="administration">
        <template v-if="loadingFinishing">{{ t('public.wait') }}</template>
        <template v-else>{{ t('public.finish') }}</template>
      </div>
    </template>
  </div>
  <footer class="webinar__footer">
    <div class="screen-wrapper">
      <div class="webinar__footer__left" v-html="footer.left"></div>
      <div class="webinar__footer__right" v-html="footer.right"></div>
    </div>
  </footer>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref,
} from 'vue'
import axios from 'axios'
import { Webinar, WebinarSchedule } from '@/types/webinar.d'
import AdminButtons from '@/vue/public/AdminButtons.vue'
import IconArrowAlt from '@/vue/icons/IconArrowAlt.vue'
import IconReload from '@/vue/icons/IconReload.vue'
import { useI18n } from 'vue-i18n'
import YouTubeVideo from '@/vue/public/YouTubeVideo.vue'
import { useStore } from '@/vue/store'
import Buttons from './Buttons.vue'
import Chat from './Chat.vue'

declare global {
  interface Window {
    BASE_URI: string
    footerText: {
      left: string
      right: string
    }
    webinarsLogo: string
  }
}

export default defineComponent({
  name: 'Webinar',
  props: {
    webinar: {
      type: Object as PropType<Webinar>,
      required: true,
    },
    schedule: {
      type: Object as PropType<WebinarSchedule>,
      required: true,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    YouTubeVideo,
    IconReload,
    Buttons,
    Chat,
    AdminButtons,
    IconArrowAlt,
  },
  setup(props) {
    const { t } = useI18n()
    const store = useStore()

    const administration = computed(() => store.state.userAdmin
      && !props.schedule.endAt && !props.archived)

    const loadingFinishing = ref(false)
    const utm = computed(() => store.state.userUtm)
    const fullscreen = ref(false)

    function onContextMenu(e: MouseEvent) {
      if (!store.state.userAdmin) {
        e.preventDefault()
      }
    }

    onMounted(() => document.addEventListener('contextmenu', onContextMenu))
    onBeforeUnmount(() => document.removeEventListener('contextmenu', onContextMenu))

    return {
      mainPage: window.BASE_URI,
      logo: window.webinarsLogo,
      footer: window.footerText,
      baseURI: window.BASE_URI,

      t,
      administration,
      loadingFinishing,
      fullscreen,

      changeFullscreen(value: boolean) {
        fullscreen.value = value
      },
      async finishTranslation() {
        // eslint-disable-next-line no-alert
        if (store.state.userAdmin && !props.schedule.endAt && !loadingFinishing.value && confirm(t('public.confirmFinish'))) {
          loadingFinishing.value = true
          await axios.post(`/webinars/${props.schedule.id}/finish`)
        }
      },
      reload() {
        document.location.reload()
      },
      addUtm(href: string) {
        let l: string = href
        if (utm.value) {
          l += l.includes('?') ? '&' : '?'
          l += utm.value.replace(/^\?/, '')
        }
        return l
      },
    }
  },
})
</script>

<style lang="stylus">
.webinar
  &__finish-button
    background var(--button-color)
    color var(--button-text-color)
    text-decoration none
    text-transform uppercase
    white-space nowrap
    text-overflow ellipsis
    width 250px
    max-width 100%
    font-weight bold
    padding 8px 15px
    border-radius 3px
    margin 30px 0 0 auto
    transition .2s
    cursor pointer
    text-align center

  &__body
    display flex
    justify-content space-between
    margin-bottom 20px
    width 100%

  &__chat
    width 330px

  &__buttons
    display flex
    flex-wrap wrap

  &__page
    position relative
    overflow hidden

    &--fullscreen
      fixed top left
      size 100%
      background var(--dark-bg-color)
      padding 0

      .webinar
        &__title
          display none

        &__body
          margin 0
          height 100%

        &__chat__open
          border-top-left-radius 0

      .webinar-buttons
        absolute left bottom 30px
        padding 0 10px
        z-index 30

  &__title
    font-size 1.86em
    font-weight bold
    margin-bottom 20px
    overflow hidden
    width 100%
    align-items center
    display flex

    &__text
      text-align center
      width 100%

  &__reload
    background var(--button-color)
    color var(--button-text-color)
    cursor pointer
    text-decoration none
    text-transform uppercase
    white-space nowrap
    text-overflow ellipsis
    max-width 100%
    font-weight bold
    padding 8px 15px
    border-radius 3px

  &__description
    h1
    h2
    h3
    h4
    h5
    p
    ul
    ol
    blockquote
      color inherit

    h1
    h2
    h3
    h4
    h5
    h6
      font-weight 500
      line-height 1.2
      margin-bottom .5rem

    h1
      font-size 2.25rem

    h2
      font-size 1.8rem

    h3
      font-size 1.575rem

    h4
      font-size 1.35rem

    h5
      font-size 1.125rem

    h6
      font-size .9rem

    p
    ul
    ol
    blockquote
      margin-bottom 1rem

      &:last-child
        margin-bottom 0

    ul
      list-style circle

    ol
      list-style decimal

    ul
    ol
      padding-left 20px

@media screen and (min-width: 940px)
  .webinar
    &__page--fullscreen .webinar__chat
      position absolute
      border-radius 0
      height calc(100% - 30px)
      opacity 0

      &:hover
        opacity 1

    &__description
      padding-right 350px
</style>
